/**
 * Save a composed meal — spec page 03's "My meals".
 *
 * The tab has always been there and has always been empty: `GET /v1/recipes`
 * was wired up and `POST` was not, so the only way to own a recipe was to
 * insert one by hand. This is the missing half.
 *
 * The screen is deliberately the *Add food* basket wearing a different hat —
 * search, pick, set grams, watch a running total — because composing a meal and
 * logging one are the same physical act and learning two layouts for it is a
 * cost with nothing behind it. What differs is what the total means: here it
 * describes the pot, and the number that matters is the portion underneath it.
 *
 * Two things the preview must not pretend to:
 *
 * - **Cooking loss is ignored.** A stew loses water and gets denser, so the
 *   real cooked dish is richer per 100 g than this arithmetic says. Logging by
 *   portion rather than by weight sidesteps the error, which is why the portion
 *   figure is the headline and per-100 g is not shown at all.
 * - **The server recomputes.** These numbers are a preview of what `_rebuild`
 *   will store, not the stored value; the saved food is whatever the server
 *   worked out, and rounding may differ in the last decimal.
 */

import {
  forGrams,
  parseDecimal,
  sum,
  useDeleteRecipe,
  useFoodSearch,
  useFoods,
  useRecipes,
  useSaveRecipe,
  type FoodItem,
  type Macros,
  type Recipe,
} from "@calorio/core";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useCalorio } from "../app/providers";

/** An ingredient in the editor: the food, and how much of it goes in. */
type Ingredient = { food: FoodItem; grams: number };

const per100 = (food: FoodItem): Macros => ({
  kcal: food.kcal_per_100,
  protein_g: food.protein_g_per_100,
  carbs_g: food.carbs_g_per_100,
  fat_g: food.fat_g_per_100,
});

export function RecipeEditor() {
  const { id } = useParams<{ id: string }>();
  const ctx = useCalorio();
  const recipes = useRecipes(ctx);

  const existing = id ? recipes.data?.find((recipe) => recipe.id === id) : undefined;

  // The ids are known before the foods are. Fetching them here rather than
  // inside the form keeps the form mountable only once its ingredients are
  // real, so its state can be seeded in `useState` rather than synced by effect.
  const ingredientIds = useMemo(
    () => existing?.items.map((item) => item.food_item_id) ?? [],
    [existing],
  );
  const foods = useFoods(ctx, ingredientIds);

  if (id) {
    if (recipes.isLoading) return <p className="muted">Loading…</p>;
    if (!existing) return <p className="error">No such meal.</p>;
    if (foods.isLoading) return <p className="muted">Loading…</p>;
  }

  const initial: Ingredient[] =
    existing?.items.flatMap((item) => {
      const food = foods.byId[item.food_item_id];
      return food ? [{ food, grams: Number(item.grams) }] : [];
    }) ?? [];

  return <Editor key={existing?.id ?? "new"} existing={existing} initial={initial} />;
}

function Editor({
  existing,
  initial,
}: {
  existing: Recipe | undefined;
  initial: Ingredient[];
}) {
  const ctx = useCalorio();
  const navigate = useNavigate();
  const save = useSaveRecipe(ctx);
  const remove = useDeleteRecipe(ctx);

  const [name, setName] = useState(existing?.name ?? "");
  const [portions, setPortions] = useState(String(existing?.servings_made ?? 1));
  const [ingredients, setIngredients] = useState<Ingredient[]>(initial);
  const [query, setQuery] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const results = useFoodSearch(ctx, query);

  const totalGrams = ingredients.reduce((grams, item) => grams + item.grams, 0);
  const totals = sum(ingredients.map(({ food, grams }) => forGrams(per100(food), grams)));

  const portionCount = parseDecimal(portions);
  const portionsValid = Number.isFinite(portionCount) && portionCount > 0;
  const portion: Macros | null = portionsValid
    ? {
        kcal: totals.kcal / portionCount,
        protein_g: totals.protein_g / portionCount,
        carbs_g: totals.carbs_g / portionCount,
        fat_g: totals.fat_g / portionCount,
      }
    : null;

  const setGrams = (index: number, grams: number) =>
    setIngredients((current) =>
      current.map((item, i) => (i === index ? { ...item, grams } : item)),
    );

  const submit = () => {
    setProblem(null);

    if (!name.trim()) return setProblem("Give the meal a name.");
    if (!ingredients.length) return setProblem("A meal needs at least one ingredient.");
    if (!portionsValid) return setProblem("Portions must be a number above zero.");
    // The server refuses a weightless recipe (it would divide by zero working
    // out the per-100g basis); saying so here costs one comparison.
    if (totalGrams <= 0) return setProblem("Every ingredient weighs nothing — set some grams.");

    save.mutate(
      {
        id: existing?.id,
        input: {
          name: name.trim(),
          servings_made: portionCount,
          items: ingredients.map(({ food, grams }) => ({ food_item_id: food.id, grams })),
        },
      },
      { onSuccess: () => navigate("/you") },
    );
  };

  return (
    <div className="stack">
      <header
        style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}
      >
        <h1 style={{ fontSize: "var(--step-2)", margin: 0 }}>
          {existing ? "Edit meal" : "New meal"}
        </h1>
        <button
          onClick={() => navigate(-1)}
          style={{
            border: "none",
            background: "none",
            color: "var(--ink-62)",
            fontSize: "var(--step-2)",
            lineHeight: 1,
            // A 20px glyph with 12px around it is a 44px target; the negative
            // margin gives the room back so the heading row does not move.
            padding: "var(--sp-3)",
            margin: "calc(-1 * var(--sp-3))",
          }}
          aria-label="Close"
        >
          ×
        </button>
      </header>

      <section className="card stack" style={{ gap: "var(--sp-3)" }}>
        <input
          className="field"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Sunday chilli"
          aria-label="Meal name"
          style={{ fontSize: "var(--step-1)" }}
        />

        <label className="manual__row">
          <span>
            Makes
            <span className="muted"> portions</span>
          </span>
          <input
            className="field num manual__value"
            inputMode="decimal"
            value={portions}
            onChange={(event) => setPortions(event.target.value)}
            placeholder="4"
            aria-label="Portions this makes"
          />
        </label>
        {/* Said plainly because it is the whole reason the field exists: the
            portion is what gets logged, not a weight of casserole. */}
        <p className="stat__label" style={{ margin: 0 }}>
          How many servings the whole pot makes. Logging this meal logs one
          portion.
        </p>
      </section>

      <section className="card">
        <h2 className="card__title">Ingredients</h2>

        {!ingredients.length && <p className="muted">Search below and add what goes in.</p>}

        {ingredients.map(({ food, grams }, index) => (
          <div className="entry" key={`${food.id}-${index}`}>
            <div className="entry__body">
              <div className="entry__name">
                {food.brand ? `${food.brand} ${food.name}` : food.name}
              </div>
              <input
                className="num"
                inputMode="numeric"
                value={grams}
                aria-label={`Grams of ${food.name}`}
                onChange={(event) => setGrams(index, Number(event.target.value) || 0)}
                style={{
                  width: 72,
                  border: "1px solid var(--hairline)",
                  borderRadius: "var(--r-sm)",
                  padding: "2px var(--sp-2)",
                  fontSize: "var(--step--1)",
                }}
              />
            </div>
            <div className="entry__kcal num">
              {Math.round((food.kcal_per_100 * grams) / 100)}
            </div>
            <button
              aria-label={`Remove ${food.name}`}
              onClick={() =>
                setIngredients((current) => current.filter((_, i) => i !== index))
              }
              style={{
                border: "none",
                background: "none",
                color: "var(--ink-62)",
                lineHeight: 1,
                // 44px to tap, no change to the row: see the Close button above.
                padding: "var(--sp-3)",
                margin: "calc(-1 * var(--sp-3))",
              }}
            >
              ×
            </button>
          </div>
        ))}

        {ingredients.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              paddingTop: "var(--sp-3)",
            }}
          >
            <span className="stat__label">
              {Math.round(totalGrams)} g in the pot
              {portion && <> · {Math.round(totals.kcal)} kcal total</>}
            </span>
            {portion && (
              <span className="num" style={{ fontWeight: 700, fontSize: "var(--step-2)" }}>
                {Math.round(portion.kcal)} kcal
                <span className="muted" style={{ fontSize: "var(--step--1)", fontWeight: 400 }}>
                  {" "}
                  / portion
                </span>
              </span>
            )}
          </div>
        )}

        {portion && (
          <p className="stat__label" style={{ margin: "var(--sp-1) 0 0" }}>
            per portion: P {Math.round(portion.protein_g)} · C {Math.round(portion.carbs_g)} · F{" "}
            {Math.round(portion.fat_g)}
            {/* Not a disclaimer for its own sake — someone weighing the cooked
                dish will find it denser than this, and being told why beats
                deciding the app is wrong. */}
            <span className="muted">
              {" "}
              — raw weights, before any water cooks off
            </span>
          </p>
        )}
      </section>

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search foods to add"
        aria-label="Search foods to add"
        className="field"
        style={{ fontSize: "var(--step-1)" }}
      />

      <section className="card" hidden={query.length < 2}>
        <h2 className="card__title">Results</h2>

        {results.isLoading && <p className="muted">Searching…</p>}

        {results.data?.map((food) => (
          <div className="entry" key={food.id}>
            <div className="entry__body">
              <div className="entry__name">
                {food.brand ? `${food.brand} ${food.name}` : food.name}
              </div>
              <div className="entry__meta num">
                {Math.round(food.kcal_per_100)} kcal / 100 {food.basis_unit}
              </div>
            </div>
            <button
              className="entry__add"
              aria-label={`Add ${food.name}`}
              onClick={() =>
                setIngredients((current) => [
                  ...current,
                  // The food's own default serving where it has one; 100 g is a
                  // fallback, not a guess at how much goes in the pot.
                  { food, grams: food.servings.find((s) => s.is_default)?.grams ?? 100 },
                ])
              }
            >
              +
            </button>
          </div>
        ))}

        {results.data?.length === 0 && query.length >= 2 && (
          <p className="muted">
            Nothing found. Scan or add it from the Add food screen first — a meal
            is built out of foods that already exist.
          </p>
        )}
      </section>

      {problem && (
        <p className="error" style={{ margin: 0 }}>
          {problem}
        </p>
      )}
      {save.isError && (
        <p className="error" style={{ margin: 0 }}>
          Couldn't save that meal. {save.error?.message ?? "Try again."}
        </p>
      )}

      <button className="button" onClick={submit} disabled={save.isPending}>
        {save.isPending ? "Saving…" : existing ? "Save changes" : "Save meal"}
      </button>

      {existing && (
        <>
          {/* Two-step, because a recipe is minutes of typing and the button sits
              directly under the one people mean to press. */}
          {!confirmingDelete ? (
            <button
              className="signin__secondary"
              onClick={() => setConfirmingDelete(true)}
              style={{ alignSelf: "center" }}
            >
              Delete this meal
            </button>
          ) : (
            <div className="card stack" style={{ gap: "var(--sp-3)" }}>
              <p style={{ margin: 0 }}>
                Delete “{existing.name}”? Days you've already logged it on keep
                their entries — those numbers were frozen when you logged them.
              </p>
              <div style={{ display: "flex", gap: "var(--sp-2)" }}>
                <button
                  className="button"
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(existing.id, { onSuccess: () => navigate("/you") })
                  }
                >
                  {remove.isPending ? "Deleting…" : "Delete"}
                </button>
                <button className="signin__secondary" onClick={() => setConfirmingDelete(false)}>
                  Keep it
                </button>
              </div>
              {remove.isError && (
                <p className="error" style={{ margin: 0 }}>
                  Couldn't delete that. {remove.error?.message ?? "Try again."}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
